import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'gatsby'
import { ProductData, ProductMaterial, ProductImage } from '../../types'
import { renderImageUrl } from '../../lib/imageurl_api'
import FluidProductImage from './FluidProductImage'
import { isTouchDevice , formatPrice , chooseDefaultMaterial } from '../../utils/utils'
import { isFavorite } from '../../lib/favorites_hook'
import { getThumbnailReplacementType, buildPesUrl } from '../../utils/UrlUtils'
import { getCartCountry, getPriceCountry } from '../../utils/CountrySelection'
import MotiveEdit from '../cms/MotiveEdit'
import { useSale } from '../../lib/global/sale_hook'
import { getHash } from '../../utils/Crypto'
import Favorite from './Favorite'

interface ProductPreviewProps {
  productData: ProductData | null
  materials: ProductMaterial[]
  material: string
  variant?: string
  hideTitle?: boolean
  hidePrice?: boolean
  hideMaterialSize?: boolean
  setOnClick?: any
  trackClick?: (productUrl: string) => void
}

const ProductPreview: React.FC<ProductPreviewProps> = ({
  productData,
  materials,
  material,
  variant,
  hideTitle,
  hidePrice,
  hideMaterialSize,
  setOnClick,
  trackClick,
}) => {
  const { t } = useTranslation('translation')

  const key = getHash(JSON.stringify(productData) + JSON.stringify(materials))

  if (materials.length === 0) {
    // TODO
    // console.error('Missing materials for "' + material + '"')
  }

  const [isHovering, setIsHovering] = React.useState(isTouchDevice())

  const handleMouseEnter = () => {
    if (!isTouchDevice()) {
      setIsHovering(true)
    }
  }

  const handleMouseLeave = () => {
    if (!isTouchDevice()) {
      setIsHovering(false)
    }
  }

  let imageUrl: string | null = null
  const priceCountry = getPriceCountry(getCartCountry())

  let cheapestMaterial: ProductMaterial = {
    legacy_id: '0',
    iid: 0,
    name: '...',
    material: '...',
    images: {
      small: 'https://productimages.artboxone.com/%d-PO-small.jpg',
      large: '',
      ambiente: '',
    },
    price: {
      de: 0,
    },
    url_slug: '',
  }

  let linkedMaterial: ProductMaterial = {
    legacy_id: '0',
    iid: 0,
    name: '...',
    material: '...',
    images: {
      small: 'https://productimages.artboxone.com/%d-PO-small.jpg',
      large: '',
      ambiente: '',
    },
    price: {
      de: 0,
    },
    url_slug: '',
    legacy_id: '0',
  }

  if (productData !== null) {
    let filteredMaterials = materials.filter((pm: ProductMaterial) => {
      if (pm.price[priceCountry] < 0.02) {
        return false
      } else if (productData.materialIIds.indexOf(pm.iid) !== -1) {
        return true
      } else {
        return false
      }
    })

    if (
      material == 'tshirt' ||
      material == 'sweatshirt' ||
      material == 'hoodie'
    ) {
      filteredMaterials = filteredMaterials.filter((pm: ProductMaterial) => {
        if (productData.materialIIds.indexOf(pm.iid) >= 0) {
          return true
        } else {
          return false
        }
      })
    }

    if (variant) {
      const newFilteredMaterials = filteredMaterials.filter(
        (pm: ProductMaterial) => {
          if (variant == 'ferrero') {
            return pm.material == 'ferrero' || pm.material == 'ferrero_xxl'
          } else if (variant == 'kinder') {
            return pm.material == 'kinder' || pm.material == 'kinder_xxl'
          } else {
            return pm.material == variant
          }
        },
      )

      if (newFilteredMaterials.length == 0) {
        filteredMaterials = filteredMaterials.filter((pm: ProductMaterial) => {
          return pm.url_slug == variant
        })
      } else {
        filteredMaterials = newFilteredMaterials
      }
    }

    if (filteredMaterials.length > 0) {
      cheapestMaterial = chooseDefaultMaterial(filteredMaterials, 0)
      linkedMaterial = chooseDefaultMaterial(filteredMaterials, 1)

      if (cheapestMaterial.out_of_stock && !linkedMaterial.out_of_stock) {
        cheapestMaterial = linkedMaterial
      }

      // material = cheapestMaterial.material
      const thumbnail: ProductImage = {
        template: cheapestMaterial.images.small,
        replacement: getThumbnailReplacementType(cheapestMaterial.material),
      }

      imageUrl = renderImageUrl({
        image: thumbnail,
        productData: productData,
      })
      // } else {
      //   console.log('wuuuuut?')
      //   console.log(productData)
    }
  }

  let cheapestPrice = 0.0
  let cheapestPriceUvp = 0.0

  const sale = useSale()

  let salePercent = 0
  let salePrice = 0.0
  let saleText = ''
  let priceClass = ''

  cheapestPrice = cheapestMaterial.price[priceCountry]
  cheapestPriceUvp =
    typeof cheapestMaterial.priceUvp !== 'undefined'
      ? cheapestMaterial.priceUvp[priceCountry]
      : cheapestPrice

  if (sale != undefined) {
    salePercent = sale.sale
    salePrice = (cheapestPrice / 100) * (100 - salePercent)
    salePrice = Math.round((salePrice + Number.EPSILON) * 100) / 100
    saleText = '' + sale.text
    priceClass = 'strike'
  } else if (cheapestPrice != cheapestPriceUvp) {
    salePrice = cheapestPrice
    cheapestPrice = cheapestPriceUvp
    saleText = ''
  }

  if (imageUrl !== null && productData !== null) {
    const isFav = isFavorite(productData.id, material, productData.ratio)
    // console.log(cheapestMaterial)
    const productUrl = buildPesUrl(
      material,
      linkedMaterial.url_slug,
      productData.id,
      productData.urlslug,
      productData.ratio,
    )

    const outOfStock = cheapestMaterial.out_of_stock
    const outOfStockMessage = t('- Leider ausverkauft -')

    // imageUrl.replace('productimages.artboxone.com', 'pi.artbox.one')

    const handleOnClick = (
      event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    ) => {
      setOnClick?.(event)
      trackClick?.(productUrl)
    }

    return (
      <div
        className="productPreview"
        key={key}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Favorite
          material={material}
          motiveId={productData.id}
          ratio={productData.ratio}
          show={isHovering === true || isFav === true}
        />

        <Link onClick={handleOnClick} to={productUrl}>
          <FluidProductImage
            imageUrl={'' + imageUrl}
            key={
              'FluidProductImage-' +
              getHash(productData) +
              getHash('' + imageUrl)
            }
            productData={productData}
          />
          {!hideTitle && <h4>{productData.name}</h4>}

          {hideMaterialSize && <h5>{t(material)}</h5>}

          {!hideMaterialSize && (
            <h5>
              {t('als')} {cheapestMaterial.name}
            </h5>
          )}
          {/* <Badge
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          badgeContent={saleText}
          color="primary"
        > */}

          {!hidePrice && outOfStock && (
            <p
              className={'price ' + priceClass}
              dangerouslySetInnerHTML={{
                __html: outOfStockMessage,
              }}
            ></p>
          )}
          {!hidePrice && !outOfStock && salePrice == 0 && (
            <p
              className={'price ' + priceClass}
            >
              {t('ab') + ' ' + formatPrice(cheapestPrice)}
            </p>
          )}
          {!hidePrice && !outOfStock && salePrice > 0 && (
            <p className={'price '}>
              <span
                style={{ textAlign: `center`, color: `red` }}
              >
                {t('ab') + ' ' + formatPrice(salePrice)}
              </span>
              <br />
              <span
                style={{
                  textAlign: `center`,
                  textDecoration: `line-through`,
                  color: `gray`,
                }}
              >
                {t('ab') + ' ' + formatPrice(cheapestPrice)}
              </span>
            </p>
          )}
        </Link>
        <MotiveEdit motiveId={productData.id} />

        {/* </Badge> */}
      </div>
    )
  } else {
    const base64Code =
      '/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAMCAgICAgMCAgIDAwMDBAYEBAQEBAgGBgUGCQgKCgkICQkKDA8MCgsOCwkJDRENDg8QEBEQCgwSExIQEw8QEBD/wAALCAAGAAYBAREA/8QAFAABAAAAAAAAAAAAAAAAAAAACf/EAB0QAAICAQUAAAAAAAAAAAAAAAECAxEEAAUTITH/2gAIAQEAAD8AT7CidN6zJOeRkdRSMxIU9XQ8Gv/Z'
    const base64Prefix = 'data:image/jpg;base64,'

    return (
      <div className="productPreview" key={key}>
        <p className="blur">&nbsp;</p>
        <img alt="Loading preview" src={base64Prefix + base64Code} />
        <h4 className="blur">.......</h4>
        <h5 className="blur">... {cheapestMaterial.name}</h5>
        <p className="price blur">... {cheapestPrice} ...</p>
      </div>
    )
  }
}

export default ProductPreview
